// Shared section <-> URL mapping for the role shells.
//
// WHY THIS EXISTS: Dashboard.js and ComplianceView each maintain TWO literal maps, one per
// direction (SECTION_TO_URL + URL_TO_SECTION, CV_SECTION_TO_URL + CV_URL_TO_SECTION). Adding a
// section there means editing both, and nothing enforces that they agree. They already disagree:
// Dashboard's SECTION_TO_URL maps BOTH 'employees' and 'hr-overview' to '/dashboard/hr-overview',
// so URL_TO_SECTION can only name one of them and silently drops 'employees'.
//
// Here a shell declares its sections ONCE, as an ordered list of { id, path }, and both directions
// are DERIVED from it. Adding a section is a one-line edit in one place, and the two directions
// cannot drift because there is only one of them.
//
// `path` is the sub path under the shell's base, kebab case to match the convention the COO and
// compliance shells already use ('/dashboard/new-starters', '/compliance/policies'). The DEFAULT
// section uses path '' so it resolves to the bare base URL, which is what keeps /payroll,
// /dashboard and /compliance working untouched.

/**
 * Is `pathname` the shell's base path, or somewhere beneath it?
 *
 * A shell only takes its section from the URL when it is actually mounted under its own base. That
 * matters because the same component can be rendered at another path (the finance shell lives at
 * /finance, and several test suites render it with no router path at all) - there the URL cannot
 * describe a section, so the shell must keep using its internal state rather than freezing on the
 * default. Guards against the '/payrolling' style false positive by requiring an exact match or a
 * '/' boundary.
 *
 * @param {string} pathname
 * @param {string} base
 * @returns {boolean}
 */
export function isUnderBasePath(pathname, base) {
  if (!pathname || !base) return false;
  return pathname === base || pathname.startsWith(`${base}/`);
}

/**
 * Build the section <-> path mapping for one shell from a single ordered declaration.
 *
 * @param {string} base      the shell's base path, e.g. '/payroll'
 * @param {Array<{id: string, path: string}>} sections  ordered; the one with path '' is default
 * @returns {{
 *   base: string,
 *   defaultSection: string,
 *   sectionIds: string[],
 *   paths: string[],
 *   toPath: (sectionId: string) => string|null,
 *   toSection: (pathname: string) => string|null,
 * }}
 */
export function createShellRoutes(base, sections) {
  const byId = new Map();
  const bySlug = new Map();

  sections.forEach(({ id, path }) => {
    // The default section's path is '', giving the bare base URL rather than a trailing slash.
    const url = path ? `${base}/${path}` : base;
    // First declaration wins, so an id deliberately aliased to an existing path (Dashboard's
    // 'employees' -> 'hr-overview' case) cannot overwrite the canonical owner of that URL.
    if (!byId.has(id)) byId.set(id, url);
    if (!bySlug.has(url)) bySlug.set(url, id);
  });

  const defaultEntry = sections.find(s => !s.path);
  const defaultSection = defaultEntry ? defaultEntry.id : (sections[0] && sections[0].id) || null;

  return {
    base,
    defaultSection,
    sectionIds: sections.map(s => s.id),
    paths: Array.from(bySlug.keys()),
    // section id -> URL. Null for an unknown id, so a caller can tell "no such section" apart
    // from "the default section", rather than silently navigating somewhere.
    toPath: (sectionId) => (byId.has(sectionId) ? byId.get(sectionId) : null),
    // URL -> section id. Null for an unknown path, so the shell decides what an unknown sub path
    // does rather than having a default silently baked in here.
    toSection: (pathname) => {
      if (!pathname) return null;
      // Tolerate a trailing slash so '/payroll/' behaves as '/payroll'.
      const clean = pathname.length > 1 && pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
      return bySlug.has(clean) ? bySlug.get(clean) : null;
    },
  };
}

// ── Country Lead shell (/payroll) ────────────────────────────────────────────
// The FIRST shell onto this mechanism, deliberately on its own so the approach can be judged
// before it is repeated for finance, IT, HR and employee.
//
// Section ids are the EXISTING activeSection values in FinanceView, unchanged - this task gives
// them URLs, it does not rename them. 'workflows' carries path '' because it is the section
// FinanceView already defaults to (useState('workflows')), so /payroll with no sub path lands
// exactly where it lands today.
// CLROUTE: the per-employee profile path for the Country Lead shell, nested UNDER the new starters
// section so the URL reads as a child of the list it was opened from. Follows the shape the COO
// shell already uses (/dashboard/employees/:employeeId) rather than inventing one.
//
// Kept OUT of the createShellRoutes section list deliberately: that list maps whole SECTIONS to
// paths, and every entry must round-trip id <-> path. A parameterised path has no single URL, so
// adding it there would break toPath/toSection for every caller. This is a separate, explicit
// pair of helpers instead.
export const PAYROLL_EMPLOYEE_PATH = '/payroll/new-starters/:employeeId';

/** The concrete profile URL for one employee, e.g. '/payroll/new-starters/abc-123'. */
export function payrollEmployeePath(employeeId) {
  return `/payroll/new-starters/${employeeId}`;
}

export const PAYROLL_SHELL_ROUTES = createShellRoutes('/payroll', [
  { id: 'workflows',   path: '' },              // /payroll            "Payroll Workflow" (default)
  { id: 'oversight',   path: 'oversight' },     // /payroll/oversight  "Payroll Oversight"
  { id: 'leavers',     path: 'leavers' },       // /payroll/leavers    "Leaver Payroll"
  { id: 'submissions', path: 'new-starters' },  // /payroll/new-starters  "New Starters"/"My Submissions"
  { id: 'tasks',       path: 'tasks' },         // /payroll/tasks      "Tasks" (S3TASK)
  { id: 'validation',  path: 'validation' },    // /payroll/validation "Validation"
  { id: 'myprofile',   path: 'my-profile' },    // /payroll/my-profile "My Profile"
]);

// ── Finance shell (/finance) ─────────────────────────────────────────────────
// FINROUTE: the SECOND shell onto this mechanism, judged worth repeating after the Country Lead
// migration. Same component (FinanceView), same helper, no second mechanism - the only difference
// is which declaration the one gate line in FinanceView picks.
//
// Section ids are the EXISTING activeSection values for the finance role, unchanged. 'workflows'
// carries path '' because FinanceView already defaults to it (useState('workflows')), so /finance
// with no sub path lands exactly where it lands today.
//
// PATHS DELIBERATELY MATCH the Country Lead shell's for every shared section. The four ids finance
// and the Country Lead have in common (oversight, leavers, submissions, myprofile) render the SAME
// component with the SAME meaning in both shells, so giving them different slugs under different
// bases would be an arbitrary divergence that makes the two harder to read side by side. The BASE
// already distinguishes them (/finance/... vs /payroll/...), which is the distinction that matters.
//
// 'tasks' is ABSENT here and that is not an oversight: the Tasks section belongs to the Country
// Lead's own nav (S3TASK) and does not appear in financeNavSections at all, so declaring a path
// for it would invent a URL for a section this shell cannot show. A shell declares the sections it
// actually has.
// ── Employee shell (/employee) ───────────────────────────────────────────────
// EMPROUTE: the THIRD shell onto this mechanism. Same helper, no second mechanism.
//
// 'tasks' CARRIES THE PATH 'tasks' BECAUSE IT IS ALREADY IN THE WILD, not because it was free to
// choose: email_i18n.py's "View your tasks" button (English and French) and one Task.link_url in
// employees.py all point at https://enkoportal.com/employee/tasks. That URL has already been sent
// to people. Before this declaration the /employee/* wildcard swallowed the '/tasks' tail and the
// shell fell back to its default section, so those links silently landed on the overview. The slug
// here is fixed by that existing contract and must not be renamed.
//
// Section ids are the EXISTING activeSection values in EmployeeView, unchanged. 'overview' carries
// path '' because that is what EmployeeView already defaults to (useState('overview')), so
// /employee with no sub path lands exactly where it lands today - which matters more here than in
// the other two shells, because bare /employee is written as a literal fallback in seven places in
// the frontend, in ROLE_DEFAULTS for BOTH the employee and ceo roles, and in three backend
// Task.link_url values.
//
// add-employee and submissions are the NEW STARTERS group, rendered only for a standalone
// employee/ceo shell holding can_submit_new_starters. They get paths for the same reason the
// finance shell's conditional sections do: the condition governs whether the NAV ITEM shows, not
// whether the section can render.
export const EMPLOYEE_SHELL_ROUTES = createShellRoutes('/employee', [
  { id: 'overview',     path: '' },              // /employee              "My Overview" (default)
  { id: 'tasks',        path: 'tasks' },         // /employee/tasks        "Tasks" - FIXED by the emails
  { id: 'compliance',   path: 'compliance' },    // /employee/compliance   "Compliance"
  { id: 'policies',     path: 'policies' },      // /employee/policies     "Policies"
  { id: 'personal',     path: 'personal' },      // /employee/personal     "Personal info"
  { id: 'requests',     path: 'requests' },      // /employee/requests     "App requests"
  { id: 'leave',        path: 'leave' },         // /employee/leave        "Leave"
  { id: 'grievance',    path: 'grievance' },     // /employee/grievance    "Grievance"
  { id: 'validation',   path: 'validation' },    // /employee/validation   "Validation" (manager/approver only)
  { id: 'add-employee', path: 'add-employee' },  // /employee/add-employee "Add Employee" (delegate only)
  { id: 'submissions',  path: 'submissions' },   // /employee/submissions  "My Submissions" (delegate only)
]);

// ── Technology shell (/it) ───────────────────────────────────────────────────
// ITSHELL: the FOURTH shell onto this mechanism. Same helper, no second mechanism.
//
// BASE PATH IS '/it', DELIBERATELY UNCHANGED. It is already what ROLE_DEFAULTS maps the
// technology role to, already what the route guard names, and already where a login redirect and
// FormTopBar's logo click send a technology account. Renaming it to '/technology' would mean
// touching all of those for a cosmetic gain, and would strand any bookmark on the old path. The
// wildcard only ADDS sub paths beneath the same base, so bare /it still resolves.
//
// 'requests' carries path '' so bare /it lands on Application Requested (labelled "Application
// Requests" until ITSHELLTRIM). That is the ONE piece of work this role actually owns (assign and
// move_to_testing, plus seeing the queue), so it is the only honest default: the previous landing
// screen called GET /employees/, which 403s this role, and rendered "All done" from the empty
// array that failure left behind.
//
// ITSHELLTRIM (2026-09-17): 'tasks', 'documents' and 'policies' removed from this declaration per
// Marie-Lynne's decision - the shell now shows exactly two sections. This is a NAVIGATION-ONLY
// change: nothing backing these three was a section-specific grant. Tasks assigned to a technology
// account are unaffected (assignment, in-app badge counts and email notification all read
// elsewhere, not this declaration); own documents remain reachable via My Profile -> EmployeeView,
// the same path every other shell uses (EmployeeView has always shown its own account's documents
// regardless of whether a shell also gave them a dedicated top-level section); Policy Repository is
// simply no longer offered to this role from its own shell.
//
// The already-sent task-notification email link (https://enkoportal.com/employee/tasks) can no
// longer redirect to a technology-shell tasks section, because there is none - REFUSED_PATH_REDIRECTS
// in App.js maps it to TECHNOLOGY_SHELL_ROUTES.toPath('tasks'), which now returns null for an
// unknown id (createShellRoutes' own toPath contract), so that lookup's `mapped || ROLE_DEFAULTS[...]`
// falls through to the ordinary role-default bounce, landing on /it (Application Requested) instead
// of a 404 or a blank page. That fallback is confirmed live below, not merely assumed.
export const TECHNOLOGY_SHELL_ROUTES = createShellRoutes('/it', [
  { id: 'requests',   path: '' },            // /it              "Application Requested" (default)
  { id: 'myprofile',  path: 'my-profile' },  // /it/my-profile   "My Profile"
]);

export const FINANCE_SHELL_ROUTES = createShellRoutes('/finance', [
  { id: 'workflows',   path: '' },              // /finance              "Financial workflows" (default)
  { id: 'oversight',   path: 'oversight' },     // /finance/oversight    "Payroll Oversight"
  { id: 'leavers',     path: 'leavers' },       // /finance/leavers      "Leaver Payroll"
  { id: 'validation',  path: 'validation' },    // /finance/validation   "Validation"
  { id: 'myprofile',   path: 'my-profile' },    // /finance/my-profile   "My Profile"
  { id: 'submissions', path: 'new-starters' },  // /finance/new-starters "New Starters"/"My Submissions"
  { id: 'tasks',       path: 'tasks' },         // /finance/tasks        "Tasks" (OPSTASKSALL)
]);

// ── HR shell (/hr) ───────────────────────────────────────────────────────────
// HRSHELL: the FIFTH shell onto this mechanism, and the first one for a role that previously had
// no shell at all. Before this, /hr was a SINGLE route with no wildcard, rendering StaffView with
// section={3} - the new starter PAYROLL worklist - behind a topbar with four buttons and no
// sidebar. The HR role investigation found that an hr account therefore had no Oversight, no
// task list, no HR report and no leave calendar, while the server already permitted all of them.
//
// THE BASE PATH IS '/hr', DELIBERATELY UNCHANGED. It is already what ROLE_DEFAULTS maps the role
// to and already where a login redirect sends an hr account, so bare /hr still resolves and every
// existing redirect into it still works. The wildcard only ADDS sub paths beneath the same base.
//
// 'overview' CARRIES PATH '' SO BARE /hr LANDS ON HR OVERVIEW (point 3), not on the payroll
// worklist it landed on before. That is the one honest default for this role: HR Overview reads
// GET /dashboard/, which already admits hr and is already unscoped for it.
//
// THE IDS ARE DASHBOARD'S OWN SECTION IDS, not new names. The HR shell renders the SAME Dashboard
// component parameterised by role, so an id here has to be a section id that component already
// understands, or the section would not render. That is what makes this a navigation change
// rather than a second implementation of ten screens.
//
// WHAT IS DELIBERATELY ABSENT, and why, since a reader will look for it:
//   analytics             - the COO's Analytics section is a STUB. Its own render block builds
//                           `const fns = []` and `const tasks = []` as empty literals and calls
//                           no endpoint, so it has no data for any role. Offering HR an empty
//                           screen would be worse than not offering it.
//   performance           - 5 of its 6 endpoints are HR data (task completion, completion by
//                           function, overdue by function, onboarding pipeline, summary) but the
//                           sixth is /performance/compliance-by-entity, a compliance completion
//                           rate. The task says to leave it out unless it is HR data ONLY, and it
//                           is not. Reported rather than silently included.
//   workflows, financial- and compliance-workflows, compliance, compliance-report, retention,
//   policies as a GROUP   - Finance and Compliance sections, excluded by the intended behaviour.
//                           Policy Repository is kept, as its own entry, because the task lists it.
//   requests (App requests), trade desks - neither is HR work; the trade desks are gated by the
//                           server on configured address and would not appear for hr anyway.
export const HR_SHELL_ROUTES = createShellRoutes('/hr', [
  { id: 'overview',    path: '' },                // /hr                  "Oversight" (HR Overview, default)
  { id: 'profile',     path: 'my-profile' },      // /hr/my-profile       "My Profile"
  { id: 'tasks',       path: 'tasks' },           // /hr/tasks            "Tasks"
  { id: 'validation',  path: 'validation' },      // /hr/validation       "Validation" (manager/approver only)
  { id: 'hr-report',   path: 'hr-report' },       // /hr/hr-report        "HR Report"
  { id: 'newstarters', path: 'new-starters' },    // /hr/new-starters     "New starters"
  { id: 'interns',     path: 'interns' },         // /hr/interns          "Interns"
  { id: 'leavers',     path: 'leavers' },         // /hr/leavers          "Leavers"
  { id: 'grievances',  path: 'grievances' },      // /hr/grievances       "Grievances"
  { id: 'leavecal',    path: 'leave-calendar' },  // /hr/leave-calendar   "Leave calendar"
  // ABSREG: the absence register, beside the leave calendar it complements - the calendar answers
  // "who is away this week", the register answers "every declaration, with filters and detail".
  { id: 'absreg',      path: 'absence-register' }, // /hr/absence-register "Absence register"
  // HRAPPREQ: App requests, which every other role's shell already offers. The path matches the
  // COO's own sub path ('app-requests') so the two shells name the same section the same way.
  { id: 'requests',    path: 'app-requests' },    // /hr/app-requests     "App requests"
  { id: 'policies',    path: 'policies' },        // /hr/policies         "Policy Repository"
]);
